<template>
  <div class="askq-root" v-if="enabled">
    <button
      class="askq-fab"
      type="button"
      @click="open = !open"
      :aria-expanded="open ? 'true' : 'false'"
      title="Ask a question about patient booking"
    >
      <span class="askq-fab-mark" aria-hidden="true">
        <img class="askq-fab-logo" src="/static/img/logo.png" alt="" />
      </span>
      <span class="askq-fab-label">{{ open ? 'Close' : 'Ask a question' }}</span>
    </button>

    <transition name="askq-fade">
      <div class="askq-panel" v-if="open">
        <header class="askq-head">
          <div class="askq-head-row">
            <img class="askq-logo" src="/static/img/logo.png" alt="The GP Service" />
            <button type="button" class="askq-close" @click="open = false" aria-label="Close">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <h3>Ask a question</h3>
          <p class="askq-sub">Get answers from your live booking data and current filters.</p>
          <div class="askq-page" v-if="pageContextLabel">
            Viewing: <strong>{{ pageContextLabel }}</strong>
          </div>
          <div class="askq-filters" v-if="filterChips.length">
            <span class="askq-chip" v-for="chip in filterChips" :key="chip">{{ chip }}</span>
          </div>
        </header>

        <div class="askq-body">
          <div class="askq-starters" v-if="messages.length === 0">
            <div class="askq-group" v-if="starterQuestions.length">
              <div class="askq-group-title">{{ starterTitle }}</div>
              <button
                v-for="(q, idx) in starterQuestions"
                :key="'q-' + idx"
                type="button"
                class="askq-prompt"
                @click="send(q)"
                :disabled="loading"
              >
                {{ q }}
              </button>
            </div>
          </div>

          <div class="askq-messages" ref="messages" v-else>
            <div
              v-for="(m, idx) in messages"
              :key="idx"
              class="askq-msg"
              :class="m.role"
            >
              <div class="askq-msg-label">{{ m.role === 'user' ? 'You' : 'Assistant' }}</div>
              <div
                class="askq-bubble"
                :class="{ 'askq-bubble-rich': m.role === 'assistant' }"
                v-if="m.role === 'assistant'"
                v-html="formatMessage(m.content)"
              ></div>
              <div class="askq-bubble" v-else>{{ m.content }}</div>
              <div class="askq-curl-wrap" v-if="m.curl">
                <div class="askq-curl-label">Request (curl)</div>
                <pre class="askq-curl">{{ m.curl }}</pre>
              </div>
              <div class="askq-curl-wrap" v-for="(curl, curlIdx) in (m.curls || [])" :key="'curl-' + curlIdx">
                <div class="askq-curl-label">{{ m.curls.length > 1 ? ('Failed API ' + (curlIdx + 1)) : 'Failed API (curl)' }}</div>
                <pre class="askq-curl">{{ curl }}</pre>
              </div>
              <div class="askq-attachments" v-if="m.attachments && m.attachments.length">
                <button
                  v-for="att in fileAttachments(m.attachments)"
                  :key="att.fileId"
                  type="button"
                  class="askq-dl"
                  @click="downloadAttachment(att)"
                >
                  <i class="fa" :class="att.type === 'pdf' ? 'fa-file-pdf-o' : 'fa-file-word-o'"></i>
                  Download {{ att.type === 'pdf' ? 'PDF' : 'Word' }}
                </button>
              </div>
            </div>
            <div class="askq-msg assistant" v-if="loading">
              <!-- <div class="askq-msg-label">Assistant</div> -->
              <div class="askq-bubble askq-typing">
                <span class="askq-dot"></span>
                <span class="askq-dot"></span>
                <span class="askq-dot"></span>
              </div>
            </div>
          </div>
        </div>

        <form class="askq-foot" @submit.prevent="send()">
          <input
            class="askq-input"
            v-model="draft"
            type="text"
            maxlength="2000"
            placeholder="Type your question…"
            :disabled="loading"
          />
          <button class="askq-send" type="submit" :disabled="loading || !draft.trim()" aria-label="Send">
            <i class="fa fa-arrow-up"></i>
          </button>
        </form>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { formatChatMessage } from '../../utils/formatChatMessage';
import { buildCurlFromFailedRequest } from '../../utils/buildCurlCommand';

/** 2–3 prompts only, matched to the page the admin is on. */
const PAGE_QUESTIONS = {
  overview: {
    title: 'Dashboard',
    questions: [
      'How is booking health this period?',
      'Which step has the highest drop-off?',
      'How does this period compare to the previous one?'
    ]
  },
  'live-journeys': {
    title: 'Live journeys',
    questions: [
      'Who is stuck right now and on which step?',
      'How many people are on payment right now?'
    ]
  },
  sessions: {
    title: 'All journeys',
    questions: [
      'How many journeys booked vs not booked in this date range?',
      'Break down journeys by source (Direct vs booking website vs ads).',
      'Show recent abandoned journeys on mobile.'
    ]
  },
  'session-detail': {
    title: 'This journey',
    questions: [
      'Summarise this person: name, email, phone, source, and every step.',
      'Where did they drop off and why?',
      'Show technical API failures for this journey'
    ]
  },
  funnels: {
    title: 'Funnel',
    questions: [
      'Which funnel step lost the most people?',
      'What is conversion from details to payment?'
    ]
  },
  traffic: {
    title: 'Traffic',
    questions: [
      'When is our busiest hour?',
      'Which day had the most booking starts?'
    ]
  },
  errors: {
    title: 'Errors',
    questions: [
      'What is the most common HTTP 500?',
      'Which API returned 500 most often?'
    ]
  },
  apis: {
    title: 'APIs',
    questions: [
      'Which API endpoint is slowest?',
      'Which backend calls fail most often?'
    ]
  },
  capacity: {
    title: 'Capacity',
    questions: [
      'Do we need more appointment slots?',
      'Where is demand highest versus supply?'
    ]
  },
  support: {
    title: 'Support',
    questions: [
      'What are the top support reasons?',
      'Which booking step precedes most support calls?'
    ]
  },
  issues: {
    title: 'Issues',
    questions: [
      'What problems are still open?',
      'Did our last fix reduce the target error?'
    ]
  },
  'weekly-report': {
    title: 'Weekly report',
    questions: [
      'Write a weekly summary for leadership.',
      'What changed versus last week?'
    ]
  }
};

const PAGE_LABELS = {
  overview: 'Booking health dashboard',
  'live-journeys': 'People booking right now',
  sessions: 'All booking journeys',
  'session-detail': 'This patient journey',
  funnels: 'Where people drop off',
  traffic: 'Busy times',
  errors: 'Problems patients hit',
  apis: 'API performance',
  capacity: 'Doctor slots vs demand',
  support: 'Support calls & reasons',
  issues: 'Fixes & follow-up',
  'weekly-report': 'Weekly story'
};

export default {
  name: 'AskQuestionWidget',
  props: {
    pageName: { type: String, default: 'tracking' },
    pageKey: { type: String, default: '' },
    pageSummary: { type: String, default: '' },
    filters: { type: Object, default: () => ({}) },
    enabled: { type: Boolean, default: true }
  },
  data() {
    return {
      open: false,
      loading: false,
      draft: '',
      messages: []
    };
  },
  computed: {
    normalizedPageKey() {
      const explicit = String(this.pageKey || '').toLowerCase().replace(/^\/+|\/+$/g, '');
      if (explicit) {
        return explicit.replace(/^tracking\//, '');
      }
      return this.inferPageKeyFromTitle(this.pageName);
    },
    pageContextLabel() {
      return PAGE_LABELS[this.normalizedPageKey] || this.pageName || 'Tracking';
    },
    pageQuestionConfig() {
      return PAGE_QUESTIONS[this.normalizedPageKey] || PAGE_QUESTIONS.overview;
    },
    starterTitle() {
      return (this.pageQuestionConfig && this.pageQuestionConfig.title) || 'Suggested';
    },
    starterQuestions() {
      const list = (this.pageQuestionConfig && this.pageQuestionConfig.questions) || [];
      return list.slice(0, 3);
    },
    filterChips() {
      const f = this.filters || {};
      const chips = [];
      const from = f.from || f.fromDate;
      const to = f.to || f.toDate;
      if (from) chips.push('From ' + from);
      if (to) chips.push('To ' + to);
      if (f.status) chips.push(String(f.status));
      if (f.step) chips.push(String(f.step));
      if (f.deviceType) chips.push(String(f.deviceType));
      if (f.source) chips.push('Source ' + f.source);
      if (f.entrySurface) chips.push('Entry ' + f.entrySurface);
      if (f.booked) chips.push(f.booked === 'yes' ? 'Booked' : 'Not booked');
      if (f.sessionId) chips.push('Session ' + f.sessionId);
      return chips.slice(0, 5);
    },
    historyPayload() {
      return this.messages.slice(-8).map((m) => ({
        role: m.role,
        content: m.content
      }));
    }
  },
  watch: {
    normalizedPageKey() {
      // Switching dashboard ↔ session ↔ other pages resets prompts.
      this.messages = [];
      this.draft = '';
      this.loading = false;
    }
  },
  methods: {
    inferPageKeyFromTitle(title) {
      const key = String(title || '').toLowerCase();
      if (key.indexOf('live') >= 0 || key.indexOf('right now') >= 0) return 'live-journeys';
      if (key.indexOf('patient journey') >= 0 || key.indexOf('journey details') >= 0) return 'session-detail';
      if (key.indexOf('all booking') >= 0) return 'sessions';
      if (key.indexOf('drop off') >= 0 || key.indexOf('funnel') >= 0 || key.indexOf('where people') >= 0) {
        return 'funnels';
      }
      if (key.indexOf('busy') >= 0 || key.indexOf('traffic') >= 0) return 'traffic';
      if (key.indexOf('problem') >= 0 || key.indexOf('error') >= 0) return 'errors';
      if (key.indexOf('api') >= 0) return 'apis';
      if (key.indexOf('slot') >= 0 || key.indexOf('capacity') >= 0 || key.indexOf('demand') >= 0) {
        return 'capacity';
      }
      if (key.indexOf('support') >= 0) return 'support';
      if (key.indexOf('fix') >= 0 || key.indexOf('follow-up') >= 0) return 'issues';
      if (key.indexOf('weekly') >= 0) return 'weekly-report';
      if (key.indexOf('dashboard') >= 0 || key.indexOf('health') >= 0) return 'overview';
      return 'overview';
    },
    formatMessage(content) {
      return formatChatMessage(content);
    },
    buildRequestPayload(question, outputFormat) {
      const payload = {
        question,
        pageName: this.pageContextLabel,
        pageKey: this.normalizedPageKey,
        pageSummary: this.pageSummary || undefined,
        filters: this.filters || {},
        history: this.historyPayload.slice(0, -1)
      };
      if (outputFormat) {
        payload.outputFormat = outputFormat;
      }
      return payload;
    },
    formatFailureMessage(error, fallbackText) {
      const status = error && error.response && error.response.status;
      const body = error && error.response && error.response.data;
      let detail = fallbackText || 'AI is unavailable right now. Dashboards still work.';
      if (body) {
        if (typeof body === 'string') {
          detail = body;
        } else if (body.message) {
          detail = body.message;
        } else if (body.data && body.data.answer) {
          detail = body.data.answer;
        }
      } else if (error && error.message) {
        detail = error.message;
      }
      if (status) {
        detail = `Request failed (HTTP ${status}). ${detail}`;
      }
      return detail;
    },
    buildFailedRequestCurls(failedRequests) {
      return (failedRequests || [])
        .map((req) => buildCurlFromFailedRequest(req, axios.defaults))
        .filter(Boolean);
    },
    appendFailureCurls(message, curl, failedRequests) {
      const curls = this.buildFailedRequestCurls(failedRequests);
      if (curl && !curls.length) {
        return { content: message, curl, curls: [] };
      }
      if (!curl && curls.length) {
        return { content: message, curl: null, curls };
      }
      if (curl && curls.length) {
        return { content: message, curl, curls };
      }
      return { content: message, curl: curl || null, curls: [] };
    },
    detectOutputFormat(question) {
      const q = String(question || '').toLowerCase();
      if (q.includes('pdf') && (q.includes('doc') || q.includes('word'))) return 'both';
      if (q.includes('pdf') || (q.includes('download') && q.includes('summary'))) return 'pdf';
      if (q.includes('docx') || q.includes('word doc')) return 'docx';
      return null;
    },
    fileAttachments(attachments) {
      return (attachments || []).filter((a) => a.type === 'pdf' || a.type === 'docx');
    },
    downloadAttachment(att) {
      this.$onboardingTrackingService.downloadChatExport(att.fileId, att.filename).catch(() => {});
    },
    async send(preset) {
      const question = (preset || this.draft || '').trim();
      if (!question || this.loading) return;

      const outputFormat = this.detectOutputFormat(question);

      this.messages.push({ role: 'user', content: question });
      this.draft = '';
      this.loading = true;
      this.$nextTick(this.scrollToBottom);

      const payload = this.buildRequestPayload(question, outputFormat);

      try {
        const response = await this.$onboardingTrackingService.askQuestion(payload);
        const data = (response && response.data) || {};
        const answer = data.answer || 'No answer returned.';
        const failedReqs = this.buildFailedRequestCurls(data.failedRequests);
        this.messages.push({
          role: 'assistant',
          content: answer,
          dataUsed: data.dataUsed || null,
          attachments: data.attachments || [],
          curl: null,
          curls: failedReqs
        });
      } catch (e) {
        const detail = this.formatFailureMessage(e, 'AI is unavailable right now. Dashboards still work.');
        this.messages.push({
          role: 'assistant',
          content: detail,
          dataUsed: null,
          attachments: [],
          curl: null,
          curls: []
        });
      } finally {
        this.loading = false;
        this.$nextTick(this.scrollToBottom);
      }
    },
    scrollToBottom() {
      const el = this.$refs.messages;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }
};
</script>

<style scoped>
.askq-root {
  --askq-green: #009345;
  --askq-green-dark: #007a3a;
  --askq-ink: #1a2e24;
  --askq-muted: #6b7f75;
  --askq-border: #e3ebe6;
  --askq-soft: #f6faf7;

  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 1050;
  font-family: inherit;
}

/* ── FAB ── */
.askq-fab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 0;
  border-radius: 999px;
  padding: 0.4rem 1rem 0.4rem 0.4rem;
  background: var(--askq-green);
  color: #fff;
  font-weight: 600;
  font-size: 0.875rem;
  box-shadow: 0 4px 18px rgba(0, 147, 69, 0.35);
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease, background 0.15s ease;
}

.askq-fab:hover {
  transform: translateY(-1px);
  background: var(--askq-green-dark);
  box-shadow: 0 6px 22px rgba(0, 147, 69, 0.42);
}

.askq-fab-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #fff;
  flex-shrink: 0;
  overflow: hidden;
}

.askq-fab-logo {
  height: 1.35rem;
  width: auto;
  max-width: 1.75rem;
  object-fit: contain;
  object-position: left center;
}

.askq-fab-label {
  padding-right: 0.15rem;
}

/* ── Panel ── */
.askq-panel {
  position: absolute;
  right: 0;
  bottom: calc(100% + 0.65rem);
  width: min(400px, calc(100vw - 1.5rem));
  height: min(620px, calc(100vh - 5rem));
  background: #fff;
  border: 1px solid var(--askq-border);
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.14);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.askq-head {
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid var(--askq-border);
  background: var(--askq-soft);
  flex-shrink: 0;
}

.askq-head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.65rem;
}

.askq-logo {
  height: 1.75rem;
  width: auto;
  object-fit: contain;
}

.askq-close {
  border: 0;
  background: transparent;
  color: var(--askq-muted);
  font-size: 1.1rem;
  line-height: 1;
  padding: 0.25rem;
  cursor: pointer;
  border-radius: 6px;
}

.askq-close:hover {
  color: var(--askq-ink);
  background: rgba(0, 0, 0, 0.05);
}

.askq-head h3 {
  margin: 0 0 0.2rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--askq-ink);
}

.askq-sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--askq-muted);
  line-height: 1.4;
}

.askq-page {
  margin-top: 0.45rem;
  font-size: 0.75rem;
  color: var(--askq-muted);
}

.askq-page strong {
  color: var(--askq-ink);
}

.askq-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.55rem;
}

.askq-chip {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #fff;
  border: 1px solid var(--askq-border);
  color: var(--askq-muted);
}

/* ── Body ── */
.askq-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.askq-starters {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem 1rem;
  -webkit-overflow-scrolling: touch;
}

.askq-group {
  margin-bottom: 1rem;
}

.askq-group:last-child {
  margin-bottom: 0;
}

.askq-group-title {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--askq-muted);
  margin-bottom: 0.4rem;
}

.askq-prompt {
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid var(--askq-border);
  background: #fff;
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--askq-ink);
  cursor: pointer;
  margin-bottom: 0.35rem;
  transition: border-color 0.12s, background 0.12s, box-shadow 0.12s;
}

.askq-prompt:hover:not(:disabled) {
  border-color: var(--askq-green);
  background: var(--askq-soft);
  box-shadow: 0 1px 4px rgba(0, 147, 69, 0.08);
}

.askq-prompt-muted {
  color: var(--askq-muted);
  font-size: 0.8rem;
}

.askq-prompt:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Messages ── */
.askq-messages {
  flex: 1;
  overflow-y: auto;
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  -webkit-overflow-scrolling: touch;
}

.askq-msg {
  display: flex;
  flex-direction: column;
  max-width: 92%;
}

.askq-msg.user {
  align-self: flex-end;
  align-items: flex-end;
}

.askq-msg.assistant {
  align-self: flex-start;
}

.askq-msg-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--askq-muted);
  margin-bottom: 0.15rem;
}

.askq-bubble {
  padding: 0.6rem 0.8rem;
  border-radius: 12px;
  line-height: 1.45;
  font-size: 0.875rem;
}

.askq-bubble-rich >>> p,
.askq-bubble-rich /deep/ p {
  margin: 0 0 0.4rem;
}

.askq-bubble-rich >>> p:last-child,
.askq-bubble-rich /deep/ p:last-child {
  margin-bottom: 0;
}

.askq-bubble-rich >>> .askq-h,
.askq-bubble-rich /deep/ .askq-h,
.askq-bubble-rich >>> .askq-heading,
.askq-bubble-rich /deep/ .askq-heading {
  margin: 0.3rem 0 0.25rem;
  font-weight: 700;
  color: var(--askq-ink);
}

.askq-bubble-rich >>> strong,
.askq-bubble-rich /deep/ strong {
  font-weight: 700;
}

.askq-bubble-rich >>> .askq-list,
.askq-bubble-rich /deep/ .askq-list {
  margin: 0.15rem 0 0.4rem;
  padding-left: 1.1rem;
}

.askq-msg.user .askq-bubble {
  background: var(--askq-green);
  color: #fff;
  border-bottom-right-radius: 4px;
}

.askq-msg.assistant .askq-bubble {
  background: var(--askq-soft);
  color: var(--askq-ink);
  border: 1px solid var(--askq-border);
  border-bottom-left-radius: 4px;
}

.askq-typing {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--askq-muted) !important;
}

.askq-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--askq-green);
  animation: askq-pulse 1.2s infinite ease-in-out;
}

.askq-dot:nth-child(2) { animation-delay: 0.15s; }
.askq-dot:nth-child(3) { animation-delay: 0.3s; }

@keyframes askq-pulse {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
}

.askq-dl {
  margin-top: 0.35rem;
  border: 1px solid var(--askq-border);
  background: #fff;
  border-radius: 8px;
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--askq-green-dark);
  cursor: pointer;
}

.askq-dl:hover {
  background: var(--askq-soft);
  border-color: var(--askq-green);
}

.askq-dl i {
  margin-right: 0.25rem;
}

.askq-curl-wrap {
  margin-top: 0.45rem;
  max-width: 100%;
}

.askq-curl-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--askq-muted);
  margin-bottom: 0.2rem;
}

.askq-curl {
  margin: 0;
  padding: 0.55rem 0.65rem;
  border-radius: 8px;
  border: 1px solid var(--askq-border);
  background: #fff;
  color: #24352c;
  font-size: 0.6875rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
}

/* ── Input ── */
.askq-foot {
  display: flex;
  gap: 0.4rem;
  padding: 0.65rem 0.85rem 0.85rem;
  border-top: 1px solid var(--askq-border);
  background: #fff;
  flex-shrink: 0;
}

.askq-input {
  flex: 1;
  border: 1px solid var(--askq-border);
  border-radius: 999px;
  padding: 0.55rem 1rem;
  font-size: 0.875rem;
  outline: none;
  background: var(--askq-soft);
  min-width: 0;
}

.askq-input:focus {
  border-color: var(--askq-green);
  background: #fff;
  box-shadow: 0 0 0 2px rgba(0, 147, 69, 0.12);
}

.askq-send {
  width: 2.25rem;
  height: 2.25rem;
  border: 0;
  border-radius: 50%;
  background: var(--askq-green);
  color: #fff;
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  transition: background 0.12s;
}

.askq-send:hover:not(:disabled) {
  background: var(--askq-green-dark);
}

.askq-send:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.askq-fade-enter-active,
.askq-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.askq-fade-enter,
.askq-fade-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.98);
}

/* ── Mobile ── */
@media (max-width: 480px) {
  .askq-root {
    right: 0.75rem;
    bottom: 0.75rem;
    left: 0.75rem;
  }

  .askq-fab {
    width: 100%;
    justify-content: center;
    padding: 0.5rem 1rem;
  }

  .askq-panel {
    position: fixed;
    right: 0.75rem;
    left: 0.75rem;
    bottom: 4.5rem;
    width: auto;
    height: min(72vh, 560px);
    border-radius: 14px;
  }

  .askq-fab-label {
    display: inline;
  }
}

@media (min-width: 481px) {
  .askq-fab-label {
    max-width: 9rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
